import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  VStack,
  Text,
  useToast,
  useColorModeValue,
  Box,
  Progress,
  Flex,
  Spinner,
  IconButton,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import CloseModalArrow from "../../../general/CloseModalArrow";
import mockNavComparison from "./mockNavComparison";
import { useAppContext } from "../../../../contexts/AppContext";
import { Trash } from "lucide-react";

const AddNavModal = ({ onUpload, onClose, isOpen }) => {
  const { getFileIcon } = useAppContext();
  const fileInputRef = useRef();
  const toast = useToast();
  const [fileName, setFileName] = useState("");
  const [valuationDate, setValuationDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const dropBg = useColorModeValue("grey.50", "grey.700");

  const steps = [
    "Uploading file...",
    "Extracting data...",
    "Analyzing NAV...",
    "Checking: Accounting equation...",
    "Checking: Balance consistency...",
    "Checking: P&L to NAV reconciliation...",
    "Checking: Inter-period variation...",
    "Checking: Portfolio allocation coherence...",
  ];

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
  };

  const handleFileInput = (e) => {
    handleFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleSubmit = () => {
    if (!fileName || !valuationDate) {
      toast({ title: "Missing required info", status: "warning" });
      return;
    }

    setIsLoading(true);
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setCurrentStep(step);
      setProgress((step / steps.length) * 100);
      if (step >= steps.length) {
        clearInterval(interval);
        const staticNavData = {
          navComparison: mockNavComparison,
          valuationDate,
          uploadDate: new Date().toISOString(),
          status: "to review",
          sourceFile: fileName,
        };
        onUpload({
          ...staticNavData,
          isNewUpload: true,
        });
        setIsLoading(false);
        setProgress(0);
        setCurrentStep(0);
        onClose();
      }
    }, 500);
  };

  const isDisabled = !fileName || !valuationDate || isLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent borderRadius={24}>
        <ModalHeader alignItems="center" gap={4}>
          <CloseModalArrow onClose={onClose} /> Upload NAV File
        </ModalHeader>
        <ModalBody p={8}>
          {isLoading ? (
            <Flex
              textAlign="center"
              justifyContent="center"
              direction="column"
              alignItems="center"
              flex={1}
              h="100%"
              w="100%">
              <Spinner size="xl" />
              <Text mt={4}>{steps[currentStep] || "Finishing up..."}</Text>
              <Progress
                mt={4}
                size="sm"
                value={progress}
                colorScheme="purple"
                h="16px"
                borderRadius={8}
                w="320px"
              />
            </Flex>
          ) : (
            <VStack spacing={4} align="stretch">
  {/* Always render the hidden file input */}
  <Input
    type="file"
    accept=".xlsx,.xls"
    ref={fileInputRef}
    onChange={handleFileInput}
    display="none"
  />

  {!fileName ? (
    <Box
      p={6}
      borderRadius="md"
      borderWidth={1}
      borderColor="grey.200"
      bg={dropBg}
      textAlign="center"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      cursor="pointer"
      onClick={() => fileInputRef.current?.click()}
    >
      <Flex direction="column">
        Drag & drop an Excel file here or{" "}
        <Text as="span" fontWeight="600" color="brand.500">
          Click to browse
        </Text>
      </Flex>
    </Box>
  ) : (
    <Button
      w="100%"
      justifyContent="space-between"
      variant="outline"
      size="lg"
      onClick={() => fileInputRef.current?.click()}
    >
      <Flex gap={2} align="center">
        <Box w="16px">{getFileIcon(fileName)}</Box>
        <Text
          fontSize="md"
          color="grey.900"
          fontWeight="500"
          maxW="220px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {fileName}
        </Text>
      </Flex>
      <IconButton
        icon={<Trash size={14} />}
        variant="ghost"
        size="xs"
        onClick={(e) => {
          e.stopPropagation(); // évite d’ouvrir le selecteur
          setFileName(""); // reset nom fichier
          if (fileInputRef.current) {
            fileInputRef.current.value = ""; // reset champ input
          } else {
            // au cas où : attendre que le ref soit monté
            setTimeout(() => {
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }, 100);
          }
        }}
      />
    </Button>
  )}

  <Input
    placeholder="Valuation Date"
    value={valuationDate}
    onChange={(e) => setValuationDate(e.target.value)}
    type="date"
  />
  <Flex direction={"column"} gap={1}>
                <Button
                  colorScheme="purple"
                  onClick={handleSubmit}
                  isDisabled={isDisabled}>
                  Upload and review
                </Button>
              </Flex>
</VStack>

          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddNavModal;
